import {ButtonCancel, Nav, SaveButton} from '@components/faqs/StyledFAQ';
import {Box, CircularProgress, Modal, TextField, Typography} from '@mui/material';
import {styled, useTheme} from '@mui/material/styles';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

const StyledModal = styled(Modal)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

/** Tope que pide el criterio 5. El mismo que valida el backend. */
const MAX_COMMENTS = 200;

interface IProps {
    isModalOpen: boolean;
    closeModal: () => void;
    /** Renglón del listado que se está aprobando. `null` = modal cerrado. */
    expenseAccount: {folio: string; customRequest: string} | null;
    /** Devuelve el error a mostrar, o nada si salió bien. */
    onConfirm: (comments: string) => Promise<string | void>;
}

/**
 * Modal de aprobación de una Cuenta de Gastos (HU 2, criterio 5).
 *
 * Aprobar **libera el envío a SAP**, y eso no se deshace: por eso hay confirmación
 * explícita y el botón se bloquea mientras la operación está en curso, para que un doble
 * clic no dispare dos envíos.
 */
const ModalApproveExpenseAccount: React.FC<IProps> = ({
    isModalOpen,
    closeModal,
    expenseAccount,
    onConfirm,
}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const [comments, setComments] = useState('');
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState<string | null>(null);

    useEffect(() => {
        // Al abrir se limpia: si no, el comentario de la Cuenta de Gastos anterior
        // reaparecería en la siguiente aprobación.
        if (isModalOpen) {
            setComments('');
            setFailed(null);
            setSaving(false);
        }
    }, [isModalOpen]);

    const confirm = async () => {
        setSaving(true);
        setFailed(null);
        const error = await onConfirm(comments);
        setSaving(false);
        if (error) {
            setFailed(error);
            return;
        }
        closeModal();
    };

    return (
        <StyledModal open={isModalOpen} onClose={saving ? undefined : closeModal}>
            <Box
                sx={{
                    background: '#FFF',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '10px',
                    width: '520px',
                    maxWidth: '92vw',
                }}
            >
                <Nav>
                    <Typography
                        sx={{
                            fontSize: '18px',
                            fontFamily: theme.typography.fontFamily,
                            fontWeight: 700,
                            color: 'white',
                            marginLeft: '10px',
                        }}
                    >
                        {t('expense_account_validation.approve.title')}
                    </Typography>
                </Nav>

                <Box sx={{padding: '20px'}}>
                    <Typography sx={{fontSize: '13px', marginBottom: '6px'}}>
                        {t('expense_account_validation.approve.confirmation')}
                    </Typography>
                    <Typography sx={{fontSize: '13px', fontWeight: 700, marginBottom: '16px'}}>
                        {expenseAccount?.folio || expenseAccount?.customRequest}
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        label={t('expense_account_validation.approve.comments')}
                        value={comments}
                        disabled={saving}
                        // El tope se aplica también aquí y no solo al enviar: el contador
                        // sin freno deja escribir de más y el rechazo llega tarde.
                        inputProps={{maxLength: MAX_COMMENTS}}
                        onChange={(event) => setComments(event.target.value)}
                    />
                    <Typography
                        sx={{
                            fontSize: '11px',
                            textAlign: 'right',
                            marginTop: '4px',
                            color: comments.length >= MAX_COMMENTS ? '#FF5722' : '#6C6C6C',
                        }}
                    >
                        {comments.length}/{MAX_COMMENTS}
                    </Typography>

                    {failed && (
                        <Typography sx={{fontSize: '12px', color: '#FF5722', marginTop: '10px'}}>
                            {failed}
                        </Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        height: '56px',
                        alignItems: 'center',
                        margin: '0 20px 12px',
                        justifyContent: 'flex-end',
                    }}
                >
                    <ButtonCancel disabled={saving} onClick={closeModal}>
                        {t('expense_account_validation.approve.cancel').toUpperCase()}
                    </ButtonCancel>
                    <SaveButton sx={{padding: '10px'}} disabled={saving} onClick={confirm}>
                        {saving ? (
                            <CircularProgress size={16} />
                        ) : (
                            t('expense_account_validation.approve.confirm').toUpperCase()
                        )}
                    </SaveButton>
                </Box>
            </Box>
        </StyledModal>
    );
};

export default ModalApproveExpenseAccount;
